// features/finances/components/PresupuestoGastosPanel.tsx
// ============================================================================
// FINANZAS › PRESUPUESTO — captura del presupuesto de GASTOS por concepto × mes (bloque B2, 15-oct-2026, Mabel).
//
// Mabel (WhatsApp 15-oct): «tener un presupuesto de gastos así como el de ingresos… gastos presupuestados que
// regularmente son los fijos». Los fijos son REFERENCIA: aquí no se crea ningún gasto (el gasto real se sigue
// capturando en Gastos al pagarse). Cada celda: «desde este mes» (se repite cada mes hasta que se cambie) o
// «solo este mes». Las comisiones de Noli se calculan solas (cuotas de sus alumnas × su %; dic = 100 %).
// Una columna, a todo el ancho: la tabla se desplaza a lo ancho si no cabe. Lo compara Reportes › Pronóstico y
// presupuesto › «Presupuesto de gastos». API: presupuestoGastosApi.ts.
// 16-oct-2026 (Mabel, #1423): debajo de la cuadrícula, «Comparativo del mes: presupuestado vs real» con selector de mes
// (por defecto el mes en curso). Reutiliza PresupuestoGastosReporte (los cálculos los hace el servidor); se recarga al
// guardar una celda.
// 23-oct-2026 (Mabel): las pautas por clase (`padre`) van sangradas justo debajo de «Pauta publicitaria».
// ============================================================================
import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react';
import { CalendarRange, ChevronLeft, ChevronRight, Copy, Info, Repeat, Calculator, X } from 'lucide-react';
import CopiarPeriodoDialog from './CopiarPeriodoDialog';
import { moneyEntero as money } from '../api';
import {
  getCuadriculaGastos, fijarMontoGasto, quitarMontoGasto, fijarPctComision, copiarMesAnteriorGastos,
  PGCuadricula, PGFila, PGCelda,
} from '../presupuestoGastosApi';
import { Skeleton } from '../../../components/Skeleton';
import ConfirmDialog from '../../../components/ConfirmDialog';
import PresupuestoGastosReporte from '../../reports/components/PresupuestoGastosReporte';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const mesLocal = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Tijuana' }).slice(0, 7);
function sumaMeses(ym: string, n: number) { const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + n, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }
const mesLargo = (ym: string) => { const [y, m] = ym.split('-').map(Number); return `${MESES[m - 1]} ${y}`; };
const mesCorto = (ym: string) => { const [y, m] = ym.split('-').map(Number); return `${MESES[m - 1].slice(0, 3)} ${String(y).slice(2)}`; };
const sinCentavos = (n: number) => '$' + Math.round(n).toLocaleString('es-MX');
const navBtn = 'rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2 disabled:opacity-40';
const N_MESES = 6;

type Edit = { fila: PGFila; mes: string; celda: PGCelda | null };

export default function PresupuestoGastosPanel() {
  const { user } = useAuth();
  const toast = useToast();
  const puedeEditar = user?.role === 'admin';
  const [desde, setDesde] = useState(() => sumaMeses(mesLocal(), -2));
  const [d, setD] = useState<PGCuadricula | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<Edit | null>(null);
  const [copiar, setCopiar] = useState<string | null>(null);
  const [copiarPeriodo, setCopiarPeriodo] = useState(false);
  const [verVacios, setVerVacios] = useState(false);
  const [mesComp, setMesComp] = useState(mesLocal);
  const [version, setVersion] = useState(0);   // sube al guardar una celda → el comparativo se vuelve a pedir

  const cargar = useCallback(() => {
    setCargando(true); setError(null);
    getCuadriculaGastos(desde, N_MESES)
      .then((r) => { setD(r); setVersion((v) => v + 1); })
      .catch((e: any) => setError(Number(e?.status) === 404 ? 'El presupuesto de gastos se activa cuando el servidor termine de actualizarse.' : (e?.message || 'No se pudo cargar el presupuesto')))
      .finally(() => setCargando(false));
  }, [desde]);
  useEffect(() => { cargar(); }, [cargar]);

  async function hacerCopia(mes: string) {
    try {
      const r = await copiarMesAnteriorGastos(mes);
      toast.success(r.cambiados ? `${mesLargo(mes)} quedó igual que ${mesLargo(r.desde)} (${r.cambiados} concepto${r.cambiados === 1 ? '' : 's'})` : `${mesLargo(mes)} ya era igual que ${mesLargo(r.desde)}`);
      cargar();
    } catch (e: any) { toast.error(e?.message || 'No se pudo copiar'); }
  }

  if (error && !d) return <p className="rounded-xl border border-line bg-surface p-4 text-sm text-content-2"><Info size={16} className="mr-1 inline text-brand-blue" />{error}</p>;
  if (!d) return <Skeleton className="h-96 w-full rounded-2xl" />;

  const meses = d.meses.map((m) => m.mes);
  const tieneAlgo = (f: PGFila) => meses.some((m) => (f.celdas[m]?.amount || 0) !== 0) || f.calculado;

  return (
    <div className={cargando ? 'opacity-60 transition' : 'transition'}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setDesde(sumaMeses(desde, -1))} title="Un mes antes" className={navBtn}><ChevronLeft size={16} /></button>
          <span className="text-sm font-medium text-content">{mesLargo(meses[0])} – {mesLargo(meses[meses.length - 1])}</span>
          <button onClick={() => setDesde(sumaMeses(desde, 1))} title="Un mes después" className={navBtn}><ChevronRight size={16} /></button>
          {desde !== sumaMeses(mesLocal(), -2) && <button onClick={() => setDesde(sumaMeses(mesLocal(), -2))} className={`${navBtn} text-xs`}>Hoy</button>}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-content-2">
            <input type="checkbox" checked={verVacios} onChange={(e) => setVerVacios(e.target.checked)} className="accent-current" />
            Ver todos los conceptos del catálogo
          </label>
          {puedeEditar && d.disponible && (
            <button onClick={() => setCopiarPeriodo(true)} className={`${navBtn} flex items-center gap-1.5 text-xs`} data-testid="btn-copiar-periodo">
              <CalendarRange size={14} /> Copiar un periodo…
            </button>
          )}
        </div>
      </div>

      {!d.disponible && <p className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">{d.aviso}</p>}

      <p className="mb-3 text-sm text-content-3">
        Lo que esperan gastar cada mes, por concepto. Es la referencia contra la que Reportes compara lo real; aquí no se registra ningún gasto
        (los gastos se siguen capturando en Gastos al pagarlos).{puedeEditar ? ' Toca una celda para cambiarla.' : ''}
      </p>

      <div className="overflow-x-auto rounded-2xl border border-line bg-surface shadow-sm">
        <table className="w-full min-w-[860px] text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-3">
              <th className="sticky left-0 z-10 bg-surface px-4 py-2 font-medium">Concepto</th>
              {d.meses.map((m) => (
                <th key={m.mes} className={`px-2 py-2 text-right font-medium ${m.enCurso ? 'text-brand-blue' : ''}`}>
                  <div className="flex items-center justify-end gap-1">
                    {mesCorto(m.mes)}{m.enCurso ? ' ·hoy' : ''}
                    {puedeEditar && d.disponible && (
                      <button onClick={() => setCopiar(m.mes)} title={`Copiar a ${mesLargo(m.mes)} lo de ${mesLargo(sumaMeses(m.mes, -1))}`}
                        className="rounded p-0.5 text-content-3 transition hover:bg-surface-2 hover:text-content"><Copy size={12} /></button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {d.secciones.map((s) => {
              const cats = s.categorias.map((c) => ({ ...c, filas: c.conceptos.filter((f) => verVacios ? f.active || tieneAlgo(f) : tieneAlgo(f)) })).filter((c) => c.filas.length);
              if (!cats.length) return null;
              return (
                <Fragment key={s.section}>
                  <tr><td colSpan={meses.length + 1} className="bg-surface-2/70 px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-content-3">{s.label}</td></tr>
                  {cats.map((c) => (
                    <Fragment key={c.category_id}>
                      <tr className="border-b border-line/60 bg-surface-2/30 font-semibold">
                        <td className="sticky left-0 z-10 bg-surface px-4 py-1.5 text-content">{c.name}</td>
                        {meses.map((m) => <td key={m} className="px-2 py-1.5 text-right tabular-nums text-content">{c.totales[m] ? sinCentavos(c.totales[m]) : '—'}</td>)}
                      </tr>
                      {c.filas.map((f) => (
                        <tr key={f.key} className="border-b border-line/40">
                          <td className={`sticky left-0 z-10 bg-surface py-1.5 pr-2 text-content-2 ${f.padre ? 'pl-12 text-xs' : 'pl-8'}`}>
                            <span className="flex items-center gap-1.5">
                              {f.calculado && <Calculator size={13} className="shrink-0 text-brand-blue" />}
                              {f.padre && <span className="text-content-3">↳</span>}{f.name}{!f.active && <span className="text-xs text-content-3">(archivado)</span>}
                            </span>
                          </td>
                          {meses.map((m) => <Celda key={m} c={f.celdas[m]} calc={f.calculado} puede={puedeEditar && d.disponible} onClick={() => setEdit({ fila: f, mes: m, celda: f.celdas[m] })} />)}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-line bg-surface-2/70 font-semibold">
              <td className="sticky left-0 z-10 bg-surface px-4 py-2 text-content">Total presupuestado</td>
              {meses.map((m) => <td key={m} className="px-2 py-2 text-right tabular-nums text-content">{sinCentavos(d.totales[m] || 0)}</td>)}
            </tr>
            {d.real && (
              <tr className="text-content-3">
                <td className="sticky left-0 z-10 bg-surface px-4 py-1.5">Real registrado en Gastos</td>
                {meses.map((m) => <td key={m} className="px-2 py-1.5 text-right tabular-nums">{d.real![m] ? sinCentavos(d.real![m]) : '—'}</td>)}
              </tr>
            )}
          </tfoot>
        </table>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-content-3">
        <span><b className="text-content-2">$1,000</b> capturado en ese mes</span>
        <span className="flex items-center gap-1"><Repeat size={11} /> se repite desde un mes anterior</span>
        <span><span className="rounded bg-amber-100 px-1 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">solo</span> solo ese mes</span>
        <span className="flex items-center gap-1"><Calculator size={11} /> calculado (no se captura)</span>
      </div>
      <p className="mt-1 text-xs text-content-3">Nómina en el real: {d.regla.texto}</p>

      <section className="mt-6" data-testid="comparativo-mes">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display text-base font-bold text-content">Comparativo del mes: presupuestado vs real</h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setMesComp(sumaMeses(mesComp, -1))} title="Mes anterior" aria-label="Mes anterior" className={navBtn}><ChevronLeft size={16} /></button>
            <span className="min-w-[8.5rem] text-center text-sm font-medium capitalize text-content" data-testid="comparativo-mes-titulo">{mesLargo(mesComp)}</span>
            <button onClick={() => setMesComp(sumaMeses(mesComp, 1))} title="Mes siguiente" aria-label="Mes siguiente" className={navBtn}><ChevronRight size={16} /></button>
            {mesComp !== mesLocal() && <button onClick={() => setMesComp(mesLocal())} className={`${navBtn} text-xs`}>Este mes</button>}
          </div>
        </div>
        <PresupuestoGastosReporte key={`${mesComp}-${version}`} mes={mesComp} titulo={`Gastos de ${mesLargo(mesComp)}`} ocultarCapturar className="" />
      </section>

      {edit && (edit.fila.calculado
        ? <EditorComision e={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); cargar(); }} />
        : <EditorMonto e={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); cargar(); }} />)}
      {copiar && (
        <ConfirmDialog title={`Copiar a ${mesLargo(copiar)}`} tone="normal" confirmLabel="Copiar"
          lines={[`${mesLargo(copiar)} queda igual que ${mesLargo(sumaMeses(copiar, -1))}, concepto por concepto.`, 'Solo cambia ese mes: los meses de antes y de después se quedan como están. Las comisiones se siguen calculando solas.']}
          onConfirm={() => { const m = copiar; setCopiar(null); hacerCopia(m); }} onClose={() => setCopiar(null)} />
      )}
      {/* 25-oct-2026 (B2 ampliado): copiar un semestre (o el periodo que sea) a otro, sin ir mes por mes. */}
      {copiarPeriodo && <CopiarPeriodoDialog tipo="gastos" desdeInicial={meses[0]} onClose={() => setCopiarPeriodo(false)} onCopiado={() => { setCopiarPeriodo(false); cargar(); }} />}
    </div>
  );
}

function Celda({ c, calc, puede, onClick }: { c: PGCelda | null; calc: boolean; puede: boolean; onClick: () => void }) {
  const cls = `px-2 py-1.5 text-right tabular-nums ${puede ? 'cursor-pointer hover:bg-accent/10' : ''}`;
  const titulo = c ? (calc ? c.detalle : c.origen === 'heredado' ? `Se repite desde ${mesLargo(c.desde || '')}` : c.origen === 'solo' ? 'Solo este mes' : 'Capturado en este mes; se repite en los siguientes') + (c.note ? ` · ${c.note}` : '') : 'Sin presupuesto';
  return (
    <td className={cls} title={titulo} onClick={puede ? onClick : undefined}>
      {!c ? <span className="text-content-3">—</span>
        : calc ? <span className="italic text-content-2">{sinCentavos(c.amount)}{c.estimado ? '*' : ''}{c.pctDelMes != null ? <span className="ml-1 text-xs text-brand-blue">{c.pct} %</span> : null}</span>
        : c.origen === 'heredado' ? <span className="text-content-3">{c.amount ? sinCentavos(c.amount) : '$0'}</span>
        : <span className="font-medium text-content">{sinCentavos(c.amount)}{c.origen === 'solo' && <span className="ml-1 rounded bg-amber-100 px-1 text-[10px] font-normal text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">solo</span>}</span>}
    </td>
  );
}

function Modal({ titulo, onClose, children }: { titulo: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative flex max-h-[92vh] w-full max-w-md flex-col rounded-t-2xl border border-line bg-surface shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <h3 className="font-display text-lg font-bold text-content">{titulo}</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <div className="space-y-3 overflow-y-auto px-5 py-4 text-sm">{children}</div>
      </div>
    </div>
  );
}
const input = 'w-full rounded-lg border border-line bg-surface px-3 py-2 text-content outline-none focus:border-brand-blue';
const btn = 'rounded-lg px-4 py-2 text-sm font-medium transition disabled:opacity-50';

function EditorMonto({ e, onClose, onSaved }: { e: Edit; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const c = e.celda;
  const [monto, setMonto] = useState(c ? String(c.amount) : '');
  const [modo, setModo] = useState<'desde' | 'solo'>(c?.origen === 'solo' ? 'solo' : 'desde');
  const [nota, setNota] = useState(c?.note || '');
  const [guardando, setGuardando] = useState(false);
  const propio = c && (c.origen === 'desde' || c.origen === 'solo');
  async function guardar() {
    const a = Number(String(monto).replace(/[$,\s]/g, ''));
    if (!Number.isFinite(a) || a < 0) { toast.error('Escribe un monto válido'); return; }
    setGuardando(true);
    try { await fijarMontoGasto({ concept_id: e.fila.concept_id!, month: e.mes, amount: a, modo, note: nota.trim() || null }); toast.success('Presupuesto guardado'); onSaved(); }
    catch (er: any) { toast.error(er?.message || 'No se pudo guardar'); } finally { setGuardando(false); }
  }
  async function quitar() {
    setGuardando(true);
    try { await quitarMontoGasto(e.fila.concept_id!, e.mes); toast.success('Monto quitado'); onSaved(); }
    catch (er: any) { toast.error(er?.message || 'No se pudo quitar'); } finally { setGuardando(false); }
  }
  return (
    <Modal titulo={`${e.fila.name} · ${mesLargo(e.mes)}`} onClose={onClose}>
      <p className="text-content-3">
        {!c ? 'Este mes no tiene presupuesto.' : c.origen === 'heredado' ? `Hoy: ${money(c.amount)}, se repite desde ${mesLargo(c.desde || '')}.` : c.origen === 'solo' ? `Hoy: ${money(c.amount)}, solo este mes.` : `Hoy: ${money(c.amount)}, capturado aquí y se repite en los meses siguientes.`}
      </p>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-content-2">Monto del mes</span>
        <input autoFocus inputMode="decimal" value={monto} onChange={(ev) => setMonto(ev.target.value)} onKeyDown={(ev) => ev.key === 'Enter' && guardar()} className={input} placeholder="0.00" />
      </label>
      <fieldset className="space-y-1.5">
        <label className="flex items-start gap-2"><input type="radio" checked={modo === 'desde'} onChange={() => setModo('desde')} className="mt-1" />
          <span><b className="text-content">Desde {mesLargo(e.mes)} en adelante</b><span className="block text-xs text-content-3">Se repite cada mes hasta que lo cambies. Los meses anteriores no cambian.</span></span></label>
        <label className="flex items-start gap-2"><input type="radio" checked={modo === 'solo'} onChange={() => setModo('solo')} className="mt-1" />
          <span><b className="text-content">Solo {mesLargo(e.mes)}</b><span className="block text-xs text-content-3">Un mes especial (por ejemplo, un bono). Los demás meses se quedan como están.</span></span></label>
      </fieldset>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-content-2">Nota (opcional)</span>
        <input value={nota} onChange={(ev) => setNota(ev.target.value)} maxLength={190} className={input} placeholder="Ej. Agua 650 + Internet 600" />
      </label>
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        {propio ? <button onClick={quitar} disabled={guardando} className={`${btn} text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40`} title="Este mes vuelve a tomar el monto del mes anterior que se repite">Quitar este monto</button> : <span />}
        <div className="flex gap-2">
          <button onClick={onClose} className={`${btn} text-content-2 hover:bg-surface-2`}>Cancelar</button>
          <button onClick={guardar} disabled={guardando} className={`${btn} bg-accent text-on-accent`}>Guardar</button>
        </div>
      </div>
    </Modal>
  );
}

function EditorComision({ e, onClose, onSaved }: { e: Edit; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const c = e.celda;
  const [pct, setPct] = useState(String(c?.pct ?? e.fila.pctTarifa ?? ''));
  const [guardando, setGuardando] = useState(false);
  async function guardar(valor: number | null) {
    if (valor != null && (!Number.isFinite(valor) || valor < 0 || valor > 100)) { toast.error('El % va de 0 a 100'); return; }
    setGuardando(true);
    try { await fijarPctComision({ user_id: e.fila.user_id!, month: e.mes, pct: valor }); toast.success('Comisión actualizada'); onSaved(); }
    catch (er: any) { toast.error(er?.message || 'No se pudo guardar'); } finally { setGuardando(false); }
  }
  return (
    <Modal titulo={`${e.fila.name} · ${mesLargo(e.mes)}`} onClose={onClose}>
      <p className="text-content-3">Se calcula sola: {c?.detalle || 'cuotas de sus alumnas × su %'} = <b className="text-content">{money(c?.amount || 0)}</b>.{c?.estimado ? ' Es un estimado: ese mes aún no tiene sus cuotas.' : ''}</p>
      <label className="block">
        <span className="mb-1 block text-xs font-medium text-content-2">% de las cuotas en {mesLargo(e.mes)} (su tarifa: {e.fila.pctTarifa} %)</span>
        <input autoFocus inputMode="decimal" value={pct} onChange={(ev) => setPct(ev.target.value)} className={input} />
      </label>
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        {c?.pctDelMes != null ? <button onClick={() => guardar(null)} disabled={guardando} className={`${btn} text-content-2 hover:bg-surface-2`}>Usar el de su tarifa</button> : <span />}
        <div className="flex gap-2">
          <button onClick={onClose} className={`${btn} text-content-2 hover:bg-surface-2`}>Cancelar</button>
          <button onClick={() => guardar(Number(pct))} disabled={guardando} className={`${btn} bg-accent text-on-accent`}>Guardar</button>
        </div>
      </div>
    </Modal>
  );
}
